import { useEffect, useState } from "react";
import { Trophy, Award, MapPin, Heart, Loader2, Dog } from "lucide-react";
import { getCampaignTopDonors } from "../lib/api";

const formatINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const RANK_META = [
  { icon: Dog, ring: "from-[#F5C24A] to-[#E29A2F]", chip: "bg-[#F5C24A] text-[#5a3a05]", label: "#1", badge: "TOP RESCUER" },
  { icon: Trophy, ring: "from-[#C9CDD3] to-[#8E96A0]", chip: "bg-[#C9CDD3] text-[#3d454f]", label: "#2", badge: "SILVER" },
  { icon: Award, ring: "from-[#D48B62] to-[#A76438]", chip: "bg-[#D48B62] text-white", label: "#3", badge: "BRONZE" },
];

export default function TopDonors({ slug = "flood_relief", refreshKey = 0, onQuickDonate, loading = false }) {
  const [donors, setDonors] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await getCampaignTopDonors(slug, 5);
        if (!cancelled) {
          setDonors(data || []);
          setLoaded(true);
        }
      } catch {
        if (!cancelled) setLoaded(true);
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [slug, refreshKey]);

  if (!loaded || donors.length === 0) return null;

  const top3 = donors.slice(0, 3);
  const rest = donors.slice(3);

  return (
    <div
      className="relative bg-gradient-to-br from-forest to-[#0f2416] rounded-3xl p-6 lg:p-7 shadow-[0_20px_50px_rgba(30,59,39,0.25)] overflow-hidden"
      data-testid="top-donors"
    >
      {/* subtle grain */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "12px 12px" }} />

      <div className="relative flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5C24A] font-bold mb-1">
            Hall of Kindness · This Month
          </div>
          <div
            className="text-white text-xl lg:text-2xl leading-tight"
            style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 900 }}
          >
            Our Top Rescuers
          </div>
        </div>
        <Heart className="w-6 h-6 text-terracotta fill-terracotta" />
      </div>

      {/* Podium — #2 | #1 (tall) | #3 */}
      {top3.length >= 3 && (
        <div className="relative grid grid-cols-3 gap-2.5 items-end mb-4 pt-8" data-testid="top-donors-podium">
          {[1, 0, 2].map((rankIdx) => {
            const d = top3[rankIdx];
            const meta = RANK_META[rankIdx];
            const Icon = meta.icon;
            const isFirst = rankIdx === 0;
            return (
              <div key={rankIdx} className="flex flex-col items-center text-center" data-testid={`podium-${rankIdx + 1}`}>
                {/* Avatar circle */}
                <div className="relative mb-2">
                  {isFirst && (
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap flex items-center gap-1 bg-white text-forest text-[10px] font-bold px-2.5 py-1 rounded-full shadow-[0_4px_12px_rgba(0,0,0,0.15)]" data-testid="thank-you-chip">
                      <Icon className="w-3 h-3" />
                      <span>Thank you!</span>
                    </div>
                  )}
                  <div className={`p-[3px] rounded-full bg-gradient-to-br ${meta.ring}`}>
                    <div className={`${isFirst ? "w-16 h-16" : "w-12 h-12"} rounded-full bg-[#0f2416] flex items-center justify-center`}>
                      <span className={`${isFirst ? "text-xl" : "text-base"} text-white`} style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 900 }}>
                        {d.name?.charAt(0) || "?"}
                      </span>
                    </div>
                  </div>
                </div>
                {/* Rank chip */}
                <div className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${meta.chip} mb-1 tracking-wider`}>{meta.badge}</div>
                {/* Name */}
                <div className={`${isFirst ? "text-base" : "text-sm"} text-white font-bold truncate max-w-full leading-tight`}>{d.name}</div>
                {/* Amount */}
                <div className={`${isFirst ? "text-lg" : "text-sm"} text-[#F5C24A] font-black tabular-nums mt-0.5`} style={{ fontFamily: "'Nunito', sans-serif" }}>
                  {formatINR(d.amount_inr)}
                </div>
                {d.city && (
                  <div className="text-[10px] text-white/50 flex items-center gap-0.5 mt-0.5">
                    <MapPin className="w-2.5 h-2.5" />
                    {d.city}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Rest (#4, #5) as slim rows */}
      {rest.length > 0 && (
        <div className="relative space-y-2 pt-4 border-t border-white/10" data-testid="top-donors-rest">
          {rest.map((d, i) => (
            <div key={i} className="flex items-center justify-between gap-3 text-sm" data-testid={`donor-row-${i + 4}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-6 h-6 rounded-full bg-white/10 text-white/70 flex items-center justify-center text-[10px] font-bold shrink-0">
                  {i + 4}
                </div>
                <span className="text-white truncate">{d.name}</span>
                {d.city && <span className="text-white/40 text-xs truncate">· {d.city}</span>}
              </div>
              <span className="text-[#F5C24A] font-bold tabular-nums shrink-0" style={{ fontFamily: "'Nunito', sans-serif" }}>
                {formatINR(d.amount_inr)}
              </span>
            </div>
          ))}
        </div>
      )}

      {onQuickDonate && (
        <>
          <button
            type="button"
            onClick={() => onQuickDonate(9999)}
            disabled={loading}
            className="mt-5 w-full rounded-2xl px-5 py-4 bg-[#F5C24A] hover:bg-[#EFB633] text-[#3d2408] font-bold text-base leading-tight transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ fontFamily: "'Nunito', sans-serif" }}
            data-testid="quick-donate-9999"
          >
            <span className="flex items-center justify-center gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              <span>Donate ₹9,999 · Build 1 acre of dog sanctuary</span>
            </span>
          </button>

          {/* OR divider */}
          <div className="relative flex items-center my-5" data-testid="monthly-divider">
            <div className="flex-1 h-px bg-white/15" />
            <span className="px-3 text-[10px] uppercase tracking-[0.22em] text-white/50 font-bold">or</span>
            <div className="flex-1 h-px bg-white/15" />
          </div>

          {/* Monthly section */}
          <div className="rounded-2xl bg-white/5 border border-white/10 p-4" data-testid="monthly-section">
            <div className="flex items-center gap-2 mb-1.5">
              <Heart className="w-4 h-4 text-terracotta" />
              <span className="text-white text-sm font-bold" style={{ fontFamily: "'Nunito', sans-serif" }}>
                Help us monthly
              </span>
            </div>
            <p className="text-[13px] text-white/70 leading-relaxed mb-3">
              Monthly donors are the <strong className="text-white">backbone of this shelter</strong>. Your ₹3,000 every month means we never have to panic-fundraise for food or medicine — 6 dogs stay fed, vaccinated and cared for, no matter what the year brings.
            </p>
            <button
              type="button"
              onClick={() => onQuickDonate(3000, true)}
              disabled={loading}
              className="w-full rounded-xl px-5 py-3 bg-terracotta hover:bg-[#B54D35] text-white font-bold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ fontFamily: "'Nunito', sans-serif" }}
              data-testid="monthly-donate-3000"
            >
              <span className="flex items-center justify-center gap-2">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                <span>Donate ₹3,000 / month</span>
              </span>
            </button>
            <p className="text-[10px] text-white/40 text-center mt-2">
              Cancel anytime · Secured by Razorpay
            </p>
          </div>
        </>
      )}
    </div>
  );
}
